// EventFormModal.jsx — Create / Edit event modal
// Used by EventListing.jsx
import { useState, useEffect, useRef } from "react";
import {
  createExperience,
  updateExperience,
  initiateExperienceImageUpload,
  confirmExperienceCoverImage,
  confirmExperienceGallery,
  getCoverImageByExperienceId,
  getImagesByExperienceId,
  initiateItineraryItemImageUpload,
  confirmItineraryItemImageUpload,
} from "../../../../../api/farm-service/experienceService";
import { emptyForm, emptyItineraryItem, EXPERIENCE_STATUSES } from "./eventFormHelpers";
import { getFeatures } from "../../../../../api/farm-service/featureService";
import "../../../../../assets/stylesheets/EventFormModal.css";

// ── WebP conversion constants ────────────────────────────────────────────────
const SOURCE_FILE_LIMIT_MB = 15;
const SOURCE_FILE_LIMIT_BYTES = SOURCE_FILE_LIMIT_MB * 1024 * 1024;
const TARGET_UPLOAD_BYTES = 1 * 1024 * 1024;
const MAX_PROCESS_DIMENSION = 2048;
const MIN_PROCESS_DIMENSION = 640;
const INITIAL_WEBP_QUALITY = 0.8;
const MIN_WEBP_QUALITY = 0.5;
const WEBP_QUALITY_STEP = 0.1;
const SCALE_STEP = 0.85;

async function canvasToWebPFile(file, options = {}) {
  const targetBytes = options.targetBytes ?? TARGET_UPLOAD_BYTES;
  const maxDimension = options.maxDimension ?? MAX_PROCESS_DIMENSION;
  const minDimension = options.minDimension ?? MIN_PROCESS_DIMENSION;
  const initialQuality = options.quality ?? INITIAL_WEBP_QUALITY;
  const minQuality = options.minQuality ?? MIN_WEBP_QUALITY;

  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(objectUrl); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error("Image load failed")); };
    img.src = objectUrl;
  });

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas context unavailable");

  const naturalWidth = image.naturalWidth || image.width;
  const naturalHeight = image.naturalHeight || image.height;
  const naturalMaxDimension = Math.max(naturalWidth, naturalHeight);

  let scale = naturalMaxDimension > 0 ? Math.min(1, maxDimension / naturalMaxDimension) : 1;
  let quality = initialQuality;
  let bestBlob = null;

  const renderBlob = () =>
    new Promise((resolve, reject) => {
      const width = Math.max(1, Math.round(naturalWidth * scale));
      const height = Math.max(1, Math.round(naturalHeight * scale));
      canvas.width = width;
      canvas.height = height;
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(image, 0, 0, width, height);
      canvas.toBlob(
        (blob) => { blob ? resolve(blob) : reject(new Error("toBlob failed")); },
        "image/webp",
        quality
      );
    });

  while (true) {
    const blob = await renderBlob();
    if (!bestBlob || blob.size < bestBlob.size) bestBlob = blob;
    if (blob.size <= targetBytes) {
      const fileName = file.name ? file.name.replace(/\.\w+$/, ".webp") : "image.webp";
      return new File([blob], fileName, { type: "image/webp" });
    }
    if (quality - minQuality > 1e-3) {
      quality = Math.max(minQuality, quality - WEBP_QUALITY_STEP);
      continue;
    }
    const currentMaxDimension = Math.max(canvas.width, canvas.height);
    if (currentMaxDimension * SCALE_STEP >= minDimension) {
      scale *= SCALE_STEP;
      continue;
    }
    break;
  }

  if (bestBlob) {
    const fileName = file.name ? file.name.replace(/\.\w+$/, ".webp") : "image.webp";
    return new File([bestBlob], fileName, { type: "image/webp" });
  }
  throw new Error("Canvas conversion failed");
}

/**
 * Props:
 *  - farmId      {number}       required — which farm this event belongs to
 *  - event       {object|null}  null → create mode, object → edit mode
 *  - onClose     {() => void}
 *  - onCreated   {(event) => void}
 *  - onUpdated   {(event) => void}
 */
export default function EventFormModal({ farmId, event, onClose, onCreated, onUpdated }) {
  const isEdit = Boolean(event);

  const [form, setForm] = useState(() => (isEdit ? formFromEvent(event) : emptyForm()));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [allFeatures, setAllFeatures] = useState([]);

  // ── Fetch feature lookup ─────────────────────────────────────────────────
  useEffect(() => {
    getFeatures().then((res) => {
      const list = res?.data ?? res ?? [];
      setAllFeatures(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, []);

  // ── Image state ──────────────────────────────────────────────────────────
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [galleryExisting, setGalleryExisting] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [galleryRemovedIds, setGalleryRemovedIds] = useState(new Set());
  const [imageUploadStatus, setImageUploadStatus] = useState(""); // "" | "uploading"
  const [isProcessingImages, setIsProcessingImages] = useState(false);

  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  // itinerary item images: array indexed by itinerary item position
  // each slot: { file: File|null, preview: string|null, existingUrl: string|null }
  const [itineraryImages, setItineraryImages] = useState([]);
  const itineraryImgRefs = useRef([]);

  // ── Re-seed when event changes ───────────────────────────────────────────
  useEffect(() => {
    setForm(isEdit ? formFromEvent(event) : emptyForm());
    setSaveError("");
    setCoverFile(null);
    setCoverPreview(null);
    setGalleryFiles([]);
    setGalleryPreviews([]);
    setGalleryExisting([]);
    setGalleryRemovedIds(new Set());
    setImageUploadStatus("");
    setItineraryImages([]);

    if (isEdit && event?.id) {
      getCoverImageByExperienceId(event.id)
        .then((res) => setCoverPreview(res?.data?.url ?? res?.url ?? null))
        .catch(() => {});
      getImagesByExperienceId(event.id)
        .then((res) => {
          const imgs = res?.data ?? res ?? [];
          setGalleryExisting(Array.isArray(imgs) ? imgs : []);
        })
        .catch(() => {});
    }
  }, [event]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup object URLs
  useEffect(() => () => galleryPreviews.forEach((u) => URL.revokeObjectURL(u)), [galleryPreviews]);

  /* ── helpers ── */
  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function setAddressField(field, value) {
    setForm((prev) => ({ ...prev, address: { ...prev.address, [field]: value } }));
  }

  function setItineraryField(index, field, value) {
    setForm((prev) => {
      const items = prev.itineraryItems.map((it, i) =>
        i === index ? { ...it, [field]: value } : it
      );
      return { ...prev, itineraryItems: items };
    });
  }

  function addItineraryItem() {
    setForm((prev) => ({
      ...prev,
      itineraryItems: [
        ...prev.itineraryItems,
        { ...emptyItineraryItem(), sortOrder: prev.itineraryItems.length },
      ],
    }));
    setItineraryImages((prev) => [...prev, { file: null, preview: null, existingUrl: null }]);
  }

  function removeItineraryItem(index) {
    // revoke object URL if any
    const img = itineraryImages[index];
    if (img?.preview) URL.revokeObjectURL(img.preview);

    setForm((prev) => ({
      ...prev,
      itineraryItems: prev.itineraryItems
        .filter((_, i) => i !== index)
        .map((it, i) => ({ ...it, sortOrder: i })),
    }));
    setItineraryImages((prev) => prev.filter((_, i) => i !== index));
  }

  /* ── image helpers ── */
  async function handleCoverChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > SOURCE_FILE_LIMIT_BYTES) {
      alert(`Image must be under ${SOURCE_FILE_LIMIT_MB}MB before optimization.`);
      if (coverInputRef.current) coverInputRef.current.value = "";
      return;
    }
    setIsProcessingImages(true);
    try {
      const optimized = await canvasToWebPFile(file);
      if (optimized.size > TARGET_UPLOAD_BYTES) {
        alert("Unable to optimize cover image below 1MB. Please choose a smaller image.");
        if (coverInputRef.current) coverInputRef.current.value = "";
        return;
      }
      setCoverFile(optimized);
      setCoverPreview(URL.createObjectURL(optimized));
    } catch (err) {
      console.error("Cover image optimization failed:", err);
      alert("Cover image optimization failed. Please try a different file.");
      if (coverInputRef.current) coverInputRef.current.value = "";
    } finally {
      setIsProcessingImages(false);
    }
  }

  function removeCoverQueued() {
    if (coverFile) URL.revokeObjectURL(coverPreview);
    setCoverFile(null);
    if (isEdit && event?.id) {
      getCoverImageByExperienceId(event.id)
        .then((res) => setCoverPreview(res?.data?.url ?? res?.url ?? null))
        .catch(() => setCoverPreview(null));
    } else {
      setCoverPreview(null);
    }
    if (coverInputRef.current) coverInputRef.current.value = "";
  }

  const MAX_GALLERY_IMAGES = 4;
  const MAX_FEATURE_IDS = 15;

  async function handleGalleryAdd(e) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    if (galleryInputRef.current) galleryInputRef.current.value = "";

    const currentTotal = galleryExisting.filter((img) => !galleryRemovedIds.has(img.id)).length + galleryFiles.length;
    if (currentTotal >= MAX_GALLERY_IMAGES) {
      alert(`You can add a maximum of ${MAX_GALLERY_IMAGES} gallery images.`);
      return;
    }

    const slots = MAX_GALLERY_IMAGES - currentTotal;
    const toProcess = files.slice(0, slots);
    if (files.length > slots) {
      alert(`Only ${slots} more image(s) can be added. Extra files were ignored.`);
    }

    setIsProcessingImages(true);
    const added = [];
    try {
      for (const file of toProcess) {
        if (file.size > SOURCE_FILE_LIMIT_BYTES) {
          alert(`"${file.name}" exceeds ${SOURCE_FILE_LIMIT_MB}MB before optimization.`);
          continue;
        }
        try {
          const optimized = await canvasToWebPFile(file);
          if (optimized.size > TARGET_UPLOAD_BYTES) {
            alert(`"${file.name}" could not be optimized below 1MB. Please choose another image.`);
            continue;
          }
          added.push(optimized);
        } catch (err) {
          console.error(`Gallery optimization failed for ${file.name}`, err);
          alert(`"${file.name}" optimization failed. Please try another image.`);
        }
      }
    } finally {
      setIsProcessingImages(false);
    }

    if (added.length > 0) {
      const previews = added.map((f) => URL.createObjectURL(f));
      setGalleryFiles((p) => [...p, ...added]);
      setGalleryPreviews((p) => [...p, ...previews]);
    }
  }

  function removeQueuedGallery(idx) {
    URL.revokeObjectURL(galleryPreviews[idx]);
    setGalleryFiles((p) => p.filter((_, i) => i !== idx));
    setGalleryPreviews((p) => p.filter((_, i) => i !== idx));
  }

  function toggleRemoveExistingGallery(id) {
    setGalleryRemovedIds((p) => {
      const next = new Set(p);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function handleItineraryImageChange(idx, e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (file.size > SOURCE_FILE_LIMIT_BYTES) {
      alert(`Image must be under ${SOURCE_FILE_LIMIT_MB}MB before optimization.`);
      return;
    }
    setIsProcessingImages(true);
    try {
      const optimized = await canvasToWebPFile(file);
      if (optimized.size > TARGET_UPLOAD_BYTES) {
        alert("Unable to optimize this image below 1MB. Please choose a smaller image.");
        return;
      }
      const preview = URL.createObjectURL(optimized);
      setItineraryImages((prev) => {
        const next = [...prev];
        if (next[idx]?.preview) URL.revokeObjectURL(next[idx].preview);
        next[idx] = { ...next[idx], file: optimized, preview };
        return next;
      });
    } catch (err) {
      console.error(`Itinerary image optimization failed:`, err);
      alert("Image optimization failed. Please try a different file.");
    } finally {
      setIsProcessingImages(false);
    }
  }

  function removeItineraryImageQueued(idx) {
    setItineraryImages((prev) => {
      const next = [...prev];
      if (next[idx]?.preview) URL.revokeObjectURL(next[idx].preview);
      next[idx] = { ...next[idx], file: null, preview: null };
      return next;
    });
  }

  async function uploadToS3(presignedUrl, file) {
    const res = await fetch(presignedUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });
    if (!res.ok) throw new Error(`S3 upload failed (${res.status})`);
  }

  async function uploadImages(experienceId, savedItineraryItems) {
    const fid = farmId;
    const eid = experienceId;
    if (coverFile) {
      const init = await initiateExperienceImageUpload(fid, eid, { fileName: coverFile.name, contentType: coverFile.type });
      const { uploadUrl, imageId } = init?.data ?? init;
      await uploadToS3(uploadUrl, coverFile);
      await confirmExperienceCoverImage(fid, eid, imageId);
    }
    if (galleryFiles.length > 0) {
      const confirmIds = [];
      for (const file of galleryFiles) {
        const init = await initiateExperienceImageUpload(fid, eid, { fileName: file.name, contentType: file.type });
        const { uploadUrl, imageId } = init?.data ?? init;
        await uploadToS3(uploadUrl, file);
        confirmIds.push(imageId);
      }
      await confirmExperienceGallery(fid, eid, confirmIds);
    }
    // Itinerary item images — match by sortOrder index
    for (let i = 0; i < itineraryImages.length; i++) {
      const imgEntry = itineraryImages[i];
      if (!imgEntry?.file) continue;
      // Get the saved item id from the server response (matched by index)
      const itemId = savedItineraryItems?.[i]?.id;
      if (!itemId) continue;
      const init = await initiateItineraryItemImageUpload(fid, eid, itemId, {
        fileName: imgEntry.file.name,
        contentType: imgEntry.file.type,
      });
      const { uploadUrl, imageId } = init?.data ?? init;
      await uploadToS3(uploadUrl, imgEntry.file);
      await confirmItineraryItemImageUpload(fid, eid, itemId, imageId);
    }
  }

  /* ── submit ── */
  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setSaveError("");
    setImageUploadStatus("");
    try {
      const payload = {
        ...form,
        capacity: Number(form.capacity),
        durationMinutes: Number(form.durationMinutes),
        price: Number(form.price),
        banner:          form.banner          || undefined,
        featureIds:      form.featureIds.length > 0 ? form.featureIds : undefined,
        startAt:         form.startAt         || undefined,
        endAt:           form.endAt           || undefined,
        applyStartAt:    form.applyStartAt    || undefined,
        applyEndAt:      form.applyEndAt      || undefined,
        season:          form.season          || undefined,
        metaTitle:       form.metaTitle       || undefined,
        metaDescription: form.metaDescription || undefined,
        itineraryItems: form.itineraryItems.map((it, i) => ({
          ...it,
          durationMinutes: Number(it.durationMinutes),
          sortOrder: i,
        })),
      };

      const hasImages = coverFile || galleryFiles.length > 0 || itineraryImages.some((img) => img?.file);
      let experienceId;
      let savedItineraryItems;

      if (isEdit) {
        const res = await updateExperience(farmId, event.id, payload);
        experienceId = event.id;
        savedItineraryItems = res?.data?.itineraryItems ?? res?.itineraryItems;
        if (hasImages) { setImageUploadStatus("uploading"); await uploadImages(experienceId, savedItineraryItems); }
        onUpdated?.(res?.data ?? res);
      } else {
        const res = await createExperience(farmId, payload);
        experienceId = res?.data?.id ?? res?.id;
        savedItineraryItems = res?.data?.itineraryItems ?? res?.itineraryItems;
        // Backend may ignore status on create (defaults to draft), so PATCH to apply chosen status
        if (experienceId && payload.status && payload.status !== "draft") {
          try { await updateExperience(farmId, experienceId, { status: payload.status }); } catch (_) {}
        }
        if (experienceId && hasImages) { setImageUploadStatus("uploading"); await uploadImages(experienceId, savedItineraryItems); }
        onCreated?.(res?.data ?? res);
      }
      onClose();
    } catch (err) {
      setSaveError(err?.response?.data?.message ?? err?.message ?? "Failed to save. Please try again.");
      setImageUploadStatus("");
    } finally {
      setSaving(false);
    }
  }

  /* ── render ── */
  return (
    <div className="efm-backdrop" onClick={onClose}>
      <div className="efm-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="efm-header">
          <h3>
            {isEdit ? "Edit Event" : "Create Event"}
            {isEdit && <span className="efm-header-meta">ID #{event.id}</span>}
          </h3>
          <button className="efm-close" onClick={onClose} type="button" aria-label="Close">×</button>
        </div>

        <form onSubmit={handleSave}>
          <div className="efm-body">

            {/* ── Basic Info ─────────────────────────────────────────── */}
            <section className="efm-section">
              <h4 className="efm-section-title">Basic Info</h4>

              <div className="efm-form-row">
                <div className="efm-form-group efm-flex-2">
                  <label className="efm-label">Title *</label>
                  <input
                    className="efm-input"
                    required
                    placeholder="e.g. Farm Tour Experience"
                    value={form.title}
                    onChange={(e) => setField("title", e.target.value)}
                  />
                </div>
                {/* <div className="efm-form-group">
                  <label className="efm-label">Type</label>
                  <select
                    className="efm-input"
                    value={form.type}
                    onChange={(e) => setField("type", e.target.value)}
                  >
                    {["TOUR", "WORKSHOP", "HARVEST", "DINING", "CAMPING", "OTHER"].map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div> */}
                <div className="efm-form-group">
                  <label className="efm-label">Status</label>
                  <select
                    className="efm-input"
                    value={form.status}
                    onChange={(e) => setField("status", e.target.value)}
                  >
                    {EXPERIENCE_STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="efm-form-group">
                <label className="efm-label">Description</label>
                <textarea
                  className="efm-input efm-textarea"
                  rows={3}
                  placeholder="Short description of this event…"
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                />
              </div>

              <div className="efm-form-row">
                <div className="efm-form-group">
                  <label className="efm-label">Capacity</label>
                  <input
                    className="efm-input"
                    type="number"
                    min={1}
                    value={form.capacity}
                    onChange={(e) => setField("capacity", e.target.value)}
                  />
                </div>
                <div className="efm-form-group">
                  <label className="efm-label">Duration (min)</label>
                  <input
                    className="efm-input"
                    type="number"
                    min={1}
                    value={form.durationMinutes}
                    onChange={(e) => setField("durationMinutes", e.target.value)}
                  />
                </div>
                <div className="efm-form-group">
                  <label className="efm-label">Price</label>
                  <input
                    className="efm-input"
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setField("price", e.target.value)}
                  />
                </div>
                <div className="efm-form-group">
                  <label className="efm-label">Currency</label>
                  <input
                    className="efm-input"
                    value={form.currency}
                    onChange={(e) => setField("currency", e.target.value)}
                  />
                </div>
              </div>

              {/* Banner select */}
              <div className="efm-form-group" style={{ maxWidth: 240 }}>
                <label className="efm-label">Banner</label>
                <select
                  className="efm-input"
                  value={form.banner || ""}
                  onChange={(e) => setField("banner", e.target.value || false)}
                >
                  <option value="">— None —</option>
                  <option value="New">New</option>
                  <option value="Staff Pick">Staff Pick</option>
                  <option value="Trending">Trending</option>
                </select>
              </div>
            </section>

            {/* ── Features ────────────────────────────────────────────── */}
            <section className="efm-section">
              <h4 className="efm-section-title">Features</h4>
              {allFeatures.length === 0 ? (
                <p className="efm-helper-text">No features available.</p>
              ) : (
                <>
                  <p className="efm-helper-text" style={{ marginBottom: 8 }}>
                    {form.featureIds.length} / {MAX_FEATURE_IDS} selected
                    {form.featureIds.length >= MAX_FEATURE_IDS && (
                      <span style={{ color: "#ef4444", marginLeft: 8 }}>Maximum reached</span>
                    )}
                  </p>
                  <div className="efm-checklist">
                    {allFeatures.map((f) => {
                      const isChecked = form.featureIds.includes(f.id);
                      const isDisabled = !isChecked && form.featureIds.length >= MAX_FEATURE_IDS;
                      return (
                        <label
                          key={f.id}
                          className={`efm-check-row${isDisabled ? " efm-check-row--disabled" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isDisabled}
                            onChange={() => {
                              setForm((prev) => {
                                const ids = prev.featureIds ?? [];
                                return {
                                  ...prev,
                                  featureIds: ids.includes(f.id)
                                    ? ids.filter((id) => id !== f.id)
                                    : [...ids, f.id],
                                };
                              });
                            }}
                          />
                          <span>{f.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </section>

            {/* ── Scheduling ──────────────────────────────────────────── */}
            <section className="efm-section">
              <h4 className="efm-section-title">Scheduling</h4>

              <div className="efm-form-row">
                <div className="efm-form-group">
                  <label className="efm-label">Start Date &amp; Time</label>
                  <input className="efm-input" type="datetime-local" value={form.startAt} onChange={(e) => setField("startAt", e.target.value)} />
                </div>
                <div className="efm-form-group">
                  <label className="efm-label">End Date &amp; Time</label>
                  <input className="efm-input" type="datetime-local" value={form.endAt} onChange={(e) => setField("endAt", e.target.value)} />
                </div>
              </div>

              <div className="efm-form-row">
                <div className="efm-form-group">
                  <label className="efm-label">Application Open</label>
                  <input className="efm-input" type="datetime-local" value={form.applyStartAt} onChange={(e) => setField("applyStartAt", e.target.value)} />
                </div>
                <div className="efm-form-group">
                  <label className="efm-label">Application Close</label>
                  <input className="efm-input" type="datetime-local" value={form.applyEndAt} onChange={(e) => setField("applyEndAt", e.target.value)} />
                </div>
              </div>
              <div className="efm-form-group" style={{ maxWidth: 240 }}>
                <label className="efm-label">Season</label>
                <select className="efm-input" value={form.season} onChange={(e) => setField("season", e.target.value)}>
                  <option value="">— None —</option>
                  {["SPRING", "SUMMER", "FALL", "WINTER"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </section>

            {/* ── Images ──────────────────────────────────────────────── */}
            <section className="efm-section">
              <h4 className="efm-section-title">Images</h4>

              {/* Cover */}
              <p className="efm-image-sublabel">Cover Image</p>
              <div className="efm-cover-zone">
                {coverPreview ? (
                  <div className="efm-cover-preview-wrap">
                    <img src={coverPreview} alt="Cover preview" className="efm-cover-preview" />
                    {coverFile && <span className="efm-new-badge">New</span>}
                    <div className="efm-cover-actions">
                      <button type="button" className="efm-btn efm-btn-outline" onClick={() => coverInputRef.current?.click()}>Replace</button>
                      {coverFile && <button type="button" className="efm-btn efm-btn-danger" onClick={removeCoverQueued}>Remove</button>}
                    </div>
                  </div>
                ) : (
                  <button type="button" className="efm-upload-placeholder" onClick={() => coverInputRef.current?.click()}>
                    <span className="efm-upload-icon">🖼️</span>
                    <span>Click to upload cover image</span>
                    <span className="efm-upload-hint">JPG, PNG, WebP — recommended 1200×800</span>
                  </button>
                )}
                <input ref={coverInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleCoverChange} />
              </div>

              {/* Gallery */}
              <p className="efm-image-sublabel" style={{ marginTop: 20 }}>Gallery Images</p>
              <div className="efm-gallery-grid">
                {galleryExisting.map((img) => (
                  <div key={img.id} className={`efm-gallery-thumb${galleryRemovedIds.has(img.id) ? " efm-gallery-thumb--removed" : ""}`}>
                    <img src={img.url} alt="" />
                    <button type="button" className="efm-gallery-remove" onClick={() => toggleRemoveExistingGallery(img.id)} title={galleryRemovedIds.has(img.id) ? "Undo" : "Remove"}>
                      {galleryRemovedIds.has(img.id) ? "↩" : "×"}
                    </button>
                  </div>
                ))}
                {galleryPreviews.map((url, idx) => (
                  <div key={url} className="efm-gallery-thumb efm-gallery-thumb--new">
                    <img src={url} alt="" />
                    <button type="button" className="efm-gallery-remove" onClick={() => removeQueuedGallery(idx)}>×</button>
                    <span className="efm-new-badge">New</span>
                  </div>
                ))}
                <button type="button" className="efm-gallery-add" onClick={() => galleryInputRef.current?.click()}>
                  <span>＋</span>
                  <span style={{ fontSize: "0.72rem" }}>Add</span>
                </button>
              </div>
              <input ref={galleryInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleGalleryAdd} />
            </section>

            {/* ── Address ────────────────────────────────────────────── */}
            <section className="efm-section">
              <h4 className="efm-section-title">Address</h4>
              {[
                ["street1",  "Street Address"],
                ["street2",  "Address Line 2"],
                ["city",     "City"],
                ["state",    "Province / State"],
                ["region",   "Region"],
                ["zipcode",  "Zip Code"],
                ["country",  "Country"],
              ].map(([key, lbl]) => (
                <div className="efm-form-group" key={key}>
                  <label className="efm-label">{lbl}</label>
                  <input
                    className="efm-input"
                    value={form.address[key]}
                    onChange={(e) => setAddressField(key, e.target.value)}
                  />
                </div>
              ))}
            </section>

            {/* ── Itinerary ──────────────────────────────────────────── */}
            <section className="efm-section">
              <div className="efm-section-header">
                <h4 className="efm-section-title">Itinerary</h4>
                <button
                  type="button"
                  className="efm-btn efm-btn-outline"
                  onClick={addItineraryItem}
                >
                  + Add Item
                </button>
              </div>

              {form.itineraryItems.map((item, idx) => {
                const imgEntry = itineraryImages[idx];
                const imgPreview = imgEntry?.preview ?? imgEntry?.existingUrl ?? null;
                return (
                <div className="efm-itinerary-item" key={idx}>
                  <div className="efm-itinerary-header">
                    <span className="efm-itinerary-index">#{idx + 1}</span>
                    {form.itineraryItems.length > 1 && (
                      <button
                        type="button"
                        className="efm-btn efm-btn-danger"
                        onClick={() => removeItineraryItem(idx)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="efm-form-row">
                    <div className="efm-form-group efm-flex-2">
                      <label className="efm-label">Title</label>
                      <input
                        className="efm-input"
                        value={item.title}
                        onChange={(e) => setItineraryField(idx, "title", e.target.value)}
                      />
                    </div>
                    <div className="efm-form-group">
                      <label className="efm-label">Duration (min)</label>
                      <input
                        className="efm-input"
                        type="number"
                        min={1}
                        value={item.durationMinutes}
                        onChange={(e) => setItineraryField(idx, "durationMinutes", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="efm-form-group">
                    <label className="efm-label">Description</label>
                    <textarea
                      className="efm-input efm-textarea"
                      rows={2}
                      value={item.description}
                      onChange={(e) => setItineraryField(idx, "description", e.target.value)}
                    />
                  </div>

                  {/* Per-item image */}
                  <div className="efm-itinerary-img-row">
                    {imgPreview ? (
                      <div className="efm-itinerary-img-preview-wrap">
                        <img src={imgPreview} alt="Item preview" className="efm-itinerary-img-preview" />
                        {imgEntry?.file && <span className="efm-new-badge">New</span>}
                        <div className="efm-itinerary-img-actions">
                          <button
                            type="button"
                            className="efm-btn efm-btn-outline"
                            onClick={() => itineraryImgRefs.current[idx]?.click()}
                          >
                            Replace
                          </button>
                          {imgEntry?.file && (
                            <button
                              type="button"
                              className="efm-btn efm-btn-danger"
                              onClick={() => removeItineraryImageQueued(idx)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="efm-itinerary-img-placeholder"
                        onClick={() => itineraryImgRefs.current[idx]?.click()}
                      >
                        <span>📷</span>
                        <span>Add item image</span>
                      </button>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      ref={(el) => (itineraryImgRefs.current[idx] = el)}
                      onChange={(e) => handleItineraryImageChange(idx, e)}
                    />
                  </div>
                </div>
                );
              })}
            </section>


          </div>{/* /efm-body */}

          {/* Footer */}
          <div className="efm-footer">
            {isProcessingImages && (
              <p className="efm-upload-progress">🔄 Optimizing images…</p>
            )}
            {imageUploadStatus === "uploading" && (
              <p className="efm-upload-progress">⏫ Uploading images…</p>
            )}
            {saveError && <p className="efm-save-error">{saveError}</p>}
            <button
              type="button"
              className="efm-btn efm-btn-secondary"
              onClick={onClose}
              disabled={saving || isProcessingImages}
            >
              Cancel
            </button>
            <button type="submit" className="efm-btn efm-btn-primary" disabled={saving || isProcessingImages}>
              {isProcessingImages
                ? "Optimizing…"
                : saving
                ? imageUploadStatus === "uploading" ? "Uploading…" : "Saving…"
                : isEdit ? "Save Changes" : "Create Event"}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}

/* ── helper: map existing event → form shape ── */
function formFromEvent(ev) {
  return {
    title:           ev.title           ?? "",
    description:     ev.description     ?? "",
    type:            ev.type            ?? "TOUR",
    capacity:        ev.capacity        ?? 10,
    durationMinutes: ev.durationMinutes ?? 60,
    price:           ev.price           ?? 0,
    currency:        ev.currency        ?? "PHP",
    status:          ev.status          ?? "published",
    // scheduling
    startAt:         toLocalDatetime(ev.startAt),
    endAt:           toLocalDatetime(ev.endAt),
    applyStartAt:    toLocalDatetime(ev.applyStartAt),
    applyEndAt:      toLocalDatetime(ev.applyEndAt),
    season:          ev.season          ?? "",
    // meta
    banner:          ev.banner          ?? "",
    featureIds:      Array.isArray(ev.featureIds) ? ev.featureIds : (ev.features?.map((f) => f.id) ?? []),
    metaTitle:       ev.metaTitle       ?? "",
    metaDescription: ev.metaDescription ?? "",
    address: {
      street1: ev.address?.street1 ?? "",
      street2: ev.address?.street2 ?? "",
      city:    ev.address?.city    ?? "",
      state:   ev.address?.state   ?? "",
      region:  ev.address?.region  ?? "",
      zipcode: ev.address?.zipcode ?? "",
      country: ev.address?.country ?? "Philippines",
    },
    itineraryItems:
      ev.itineraryItems?.length > 0
        ? ev.itineraryItems.map((it) => ({
            title:           it.title           ?? "",
            description:     it.description     ?? "",
            durationMinutes: it.durationMinutes ?? 30,
            sortOrder:       it.sortOrder       ?? 0,
            imageId:         it.imageId         ?? null,
          }))
        : [emptyItineraryItem()],
  };
}

/** Convert ISO/date string → "YYYY-MM-DDTHH:mm" for datetime-local inputs */
function toLocalDatetime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}
